import { Check, TriangleAlert, ArrowRight, ArrowLeft, Trophy } from 'lucide-react';
import { useStore, DEFAULT_SCENARIO, BUILTIN_DRILLS } from '../store';
import { VMark } from '../components/VMark';

const STEPS = ['Teach', 'Scenario', 'Draft', 'Critique', 'Transfer'];

/** The active drill sequence: the user's report drills, or the built-in fallback. */
function useDrills() {
  const reportDrills = useStore((s) => s.report?.practiceDrills);
  return reportDrills && reportDrills.length ? reportDrills : BUILTIN_DRILLS;
}

// The prototype's fallback scenario copy for the built-in discovery-call drill.
const FALLBACK_SCENARIO =
  "You've a discovery call tomorrow with a mid-market logistics company evaluating our data platform. You have the CRM history and their last earnings note. Write a prompt that gets AI to draft your call-prep brief.";

const eyebrow: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: '#00A800',
  marginBottom: '12px',
};

const h2: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  margin: 0,
};

export function Practice() {
  const drillStep = useStore((s) => s.drillStep);
  const masteryDone = useStore((s) => s.masteryDone);
  const drillIndex = useStore((s) => s.drillIndex);
  const passedDrills = useStore((s) => s.passedDrills);
  const drills = useDrills();

  const activeDrill = drills[drillIndex] ?? drills[0];
  const drillPassed = passedDrills.includes(drillIndex);
  const segs = drills.map((_, i) => i);

  return (
    <div style={{ flex: 1, background: '#F4F4F6' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '36px 32px 80px' }}>
        {/* header + mastery */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '20px',
            flexWrap: 'wrap',
            marginBottom: '26px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                background: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}
            >
              <VMark color="#00D700" size={21} />
            </div>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>
                Practice coach
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                <span style={{ fontSize: '13.5px', color: '#75757F' }}>
                  Drill {drillIndex + 1} of {drills.length} · {activeDrill.title}
                </span>
                {drillPassed && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#00A800',
                      background: '#F6FCF6',
                      border: '1px solid #BDE8BD',
                      borderRadius: '999px',
                      padding: '2px 8px',
                    }}
                  >
                    <Check size={11} strokeWidth={3} /> Passed
                  </span>
                )}
              </div>
            </div>
          </div>
          <div
            style={{
              border: '1px solid #E2E2E6',
              borderRadius: '10px',
              background: '#fff',
              padding: '12px 16px',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#75757F',
                marginBottom: '8px',
              }}
            >
              Mastery progress
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '5px' }}>
                {segs.map((i) => (
                  <span
                    key={i}
                    style={{
                      width: '34px',
                      height: '8px',
                      borderRadius: '999px',
                      background: i < masteryDone ? '#00D700' : '#EFEFF2',
                    }}
                  />
                ))}
              </div>
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#333544' }}>
                {masteryDone} of {drills.length} consecutive drills scored 8+
              </span>
            </div>
          </div>
        </div>

        {/* stepper */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            border: '1px solid #E2E2E6',
            borderRadius: '10px',
            padding: '16px 20px',
            marginBottom: '18px',
            flexWrap: 'wrap',
          }}
        >
          {STEPS.map((label, i) => {
            const done = i < drillStep;
            const current = i === drillStep;
            const dotStyle: React.CSSProperties = {
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: 700,
              flex: 'none',
              ...(done
                ? { background: '#00D700', color: '#000' }
                : current
                  ? { background: '#000', color: '#fff' }
                  : { background: '#fff', color: '#ADAEB4', border: '1px solid #E2E2E6' }),
            };
            return (
              <div key={label} style={{ display: 'contents' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                  <span style={dotStyle}>{done ? <Check size={16} /> : i + 1}</span>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: current ? 700 : 600,
                      color: current ? '#000' : '#75757F',
                    }}
                  >
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <span
                    style={{
                      flex: 1,
                      minWidth: '14px',
                      height: '2px',
                      borderRadius: '999px',
                      margin: '0 10px',
                      background: i < drillStep ? '#00D700' : '#E2E2E6',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* step card */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #E2E2E6',
            borderRadius: '12px',
            padding: '30px 32px',
            minHeight: '320px',
          }}
        >
          <StepCard step={drillStep} />
        </div>
      </div>
    </div>
  );
}

function StepCard({ step }: { step: number }) {
  const setDrillStep = useStore((s) => s.setDrillStep);
  const draft = useStore((s) => s.draft);
  const setDraft = useStore((s) => s.setDraft);
  const submitDraft = useStore((s) => s.submitDraft);
  const startDrill = useStore((s) => s.startDrill);
  const nextDrill = useStore((s) => s.nextDrill);
  const setScreen = useStore((s) => s.setScreen);
  const scenario = useStore((s) => s.scenario);
  const drillScore = useStore((s) => s.drillScore);
  const masteryDone = useStore((s) => s.masteryDone);
  const feedback = useStore((s) => s.practiceFeedback);
  const scoring = useStore((s) => s.practiceScoring);
  const practiceError = useStore((s) => s.practiceError);
  const drillIndex = useStore((s) => s.drillIndex);
  const drills = useDrills();

  const activeDrill = drills[drillIndex] ?? drills[0];
  const isLastDrill = drillIndex >= drills.length - 1;
  const scenarioText = scenario?.trim() || FALLBACK_SCENARIO;

  if (step === 0) {
    return (
      <div className="animate-fade">
        <div style={eyebrow}>Concept</div>
        <h2 style={{ ...h2, fontSize: '26px', marginBottom: '14px' }}>A strong prompt has four parts</h2>
        <p style={{ fontSize: '16px', lineHeight: 1.6, color: '#333544', margin: '0 0 22px', maxWidth: '60ch' }}>
          The fastest way to lift any AI output is to be explicit about four things. Miss one and the model
          fills the gap with a guess.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '26px',
          }}
        >
          <Quad title="Role" body="Who the model should act as." />
          <Quad title="Context" body="The situation and the inputs it has." />
          <Quad title="Task" body="The single, specific thing to do." />
          <Quad title="Output format" body="Exactly what shape the answer should take." />
        </div>
        <PrimaryButton onClick={() => setDrillStep(1)}>
          Show me a scenario
          <ArrowRight size={17} />
        </PrimaryButton>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div className="animate-fade">
        <div style={eyebrow}>Scenario · Drill {drillIndex + 1} of {drills.length}</div>
        <h2 style={{ ...h2, marginBottom: '8px' }}>Your turn — {activeDrill.title.toLowerCase()}</h2>
        <p style={{ fontSize: '13.5px', lineHeight: 1.55, color: '#75757F', margin: '0 0 16px', maxWidth: '60ch' }}>
          What good looks like: {activeDrill.rubricLine}
        </p>
        <div
          style={{
            display: 'flex',
            gap: '13px',
            alignItems: 'flex-start',
            background: '#F4F4F6',
            borderRadius: '10px',
            borderBottomLeftRadius: '3px',
            padding: '18px 20px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            <VMark color="#00D700" size={16} />
          </div>
          <p style={{ fontSize: '15.5px', lineHeight: 1.6, color: '#000', margin: 0 }}>{scenarioText}</p>
        </div>
        <PrimaryButton onClick={() => setDrillStep(2)}>
          Draft my prompt
          <ArrowRight size={17} />
        </PrimaryButton>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="animate-fade">
        <div style={eyebrow}>Your draft</div>
        <h2 style={{ ...h2, marginBottom: '8px' }}>Write your prompt</h2>
        <p style={{ fontSize: '14px', lineHeight: 1.55, color: '#75757F', margin: '0 0 16px' }}>
          Remember the four parts — role, context, task, output format. There's no wrong answer; Avery will
          give you a rubric critique.
        </p>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Act as a presales consultant preparing for a discovery call…"
          style={{
            width: '100%',
            minHeight: '150px',
            border: '1.5px solid #E2E2E6',
            borderRadius: '10px',
            padding: '14px 16px',
            fontFamily: 'inherit',
            fontSize: '15px',
            lineHeight: 1.6,
            color: '#000',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {practiceError && (
          <p style={{ fontSize: '13px', color: '#C2453A', margin: '12px 0 0' }}>{practiceError} Please try again.</p>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginTop: '16px',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => setDrillStep(1)}
            style={{
              background: 'transparent',
              color: '#75757F',
              border: 'none',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              padding: 0,
            }}
          >
            <ArrowLeft size={15} />
            Back to scenario
          </button>
          <button
            type="button"
            onClick={() => submitDraft()}
            disabled={scoring}
            style={{
              background: '#000',
              color: '#fff',
              border: '2px solid #000',
              fontSize: '15px',
              fontWeight: 700,
              padding: '13px 24px',
              borderRadius: '6px',
              cursor: scoring ? 'default' : 'pointer',
              opacity: scoring ? 0.7 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
            }}
          >
            {scoring ? (
              'Scoring…'
            ) : (
              <>
                Submit for critique
                <ArrowRight size={17} />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  if (step === 3) {
    const score = drillScore ?? feedback?.score ?? 0;
    const met = score >= 8;
    return (
      <div className="animate-fade">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '20px',
          }}
        >
          <div>
            <div style={{ ...eyebrow, marginBottom: '8px' }}>Rubric critique</div>
            <h2 style={h2}>{met ? 'Strong draft — nearly there' : 'Good start — tighten it'}</h2>
          </div>
          <div
            style={{
              textAlign: 'center',
              border: `2px solid ${met ? '#00D700' : '#E6D9B0'}`,
              borderRadius: '12px',
              padding: '10px 20px',
              background: met ? '#F6FCF6' : '#FFFBEE',
            }}
          >
            <div
              style={{
                fontSize: '30px',
                fontWeight: 800,
                color: met ? '#00A800' : '#9A7B00',
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              {score}
              <span style={{ fontSize: '17px', color: '#75757F', fontWeight: 700 }}>/10</span>
            </div>
            <div style={{ fontSize: '11px', color: '#75757F', fontWeight: 600, marginTop: '3px' }}>
              {met ? 'Mastery threshold met' : 'Below mastery threshold'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
          {(feedback?.rubric ?? []).map((r) => (
            <Rubric key={r.label} label={r.label} ok={r.met} note={r.note} />
          ))}
        </div>

        {feedback?.improved && (
          <div
            style={{
              borderLeft: '3px solid #00D700',
              background: '#F6FCF6',
              borderRadius: '0 8px 8px 0',
              padding: '16px 18px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#00A800',
                marginBottom: '7px',
              }}
            >
              One improved version
            </div>
            <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#000', margin: 0 }}>{feedback.improved}</p>
            {feedback.whatChanged && (
              <div style={{ fontSize: '12.5px', color: '#75757F', marginTop: '9px' }}>
                <strong style={{ color: '#333544' }}>What changed:</strong> {feedback.whatChanged}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setDrillStep(2)}
            style={{
              background: '#fff',
              color: '#000',
              border: '1.5px solid #000',
              fontSize: '14px',
              fontWeight: 600,
              padding: '12px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Revise &amp; re-score
          </button>
          <button
            type="button"
            onClick={() => setDrillStep(4)}
            style={{
              // On a pass with more drills to go, "Next drill" is the primary action, so this
              // steps back to a secondary style and the green CTA below leads on.
              background: met && !isLastDrill ? '#fff' : '#00D700',
              color: '#000',
              border: `2px solid ${met && !isLastDrill ? '#C8C8CC' : '#00D700'}`,
              fontSize: '14px',
              fontWeight: 700,
              padding: '12px 22px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
            }}
          >
            Transfer to a real task
            <ArrowRight size={16} />
          </button>
          {met && !isLastDrill && (
            <button
              type="button"
              onClick={nextDrill}
              style={{
                background: '#00D700',
                color: '#000',
                border: '2px solid #00D700',
                fontSize: '14px',
                fontWeight: 700,
                padding: '12px 22px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '9px',
              }}
            >
              Next drill ({drillIndex + 2} of {drills.length})
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // step === 4 — Transfer + options
  return (
    <div className="animate-fade">
      {masteryDone >= 3 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: '#00D700',
            color: '#000',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '22px',
          }}
        >
          <Trophy size={18} style={{ flex: 'none' }} />
          <span style={{ fontSize: '14px', fontWeight: 700 }}>
            Mastery reached — 3 consecutive drills at 8+. Nice work.
          </span>
        </div>
      )}
      <div style={eyebrow}>Transfer</div>
      <h2 style={{ ...h2, marginBottom: '10px' }}>Use it on something real</h2>
      <p style={{ fontSize: '15px', lineHeight: 1.6, color: '#333544', margin: '0 0 24px', maxWidth: '60ch' }}>
        Pick a task on your plate this week and write the prompt for it now, using the four parts. That's how
        the skill sticks.
      </p>
      <div
        style={{
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#75757F',
          marginBottom: '12px',
        }}
      >
        What next?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '26px' }}>
        <OptionButton letter="A" label="Go deeper on this drill" onClick={() => setDrillStep(0)} />
        <OptionButton letter="B" label="Try a harder scenario" onClick={() => setDrillStep(1)} />
        <OptionButton
          letter="C"
          label="Apply it to a real task on my plate"
          onClick={() => startDrill(DEFAULT_SCENARIO, true)}
        />
        {isLastDrill ? (
          <OptionButton
            letter="✓"
            label="That's all 3 drills — back to my report"
            emphasised
            onClick={() => setScreen('report')}
          />
        ) : (
          <OptionButton
            letter="E"
            label={`Move to drill ${drillIndex + 2} of ${drills.length}`}
            emphasised
            onClick={nextDrill}
          />
        )}
      </div>
    </div>
  );
}

function PrimaryButton({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: '#00D700',
        color: '#000',
        border: '2px solid #00D700',
        fontSize: '15px',
        fontWeight: 700,
        padding: '13px 24px',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '9px',
      }}
    >
      {children}
    </button>
  );
}

function OptionButton({
  letter,
  label,
  onClick,
  emphasised = false,
}: {
  letter: string;
  label: string;
  onClick?: () => void;
  emphasised?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        textAlign: 'left',
        background: emphasised ? '#000' : '#fff',
        border: `1px solid ${emphasised ? '#000' : '#E2E2E6'}`,
        borderRadius: '8px',
        padding: '13px 15px',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <span
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '5px',
          background: emphasised ? '#00D700' : '#000',
          color: emphasised ? '#000' : '#fff',
          fontSize: '12px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        {letter}
      </span>
      <span style={{ fontSize: '14px', color: emphasised ? '#fff' : '#333544', fontWeight: emphasised ? 600 : 500 }}>
        {label}
      </span>
    </button>
  );
}

function Quad({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ border: '1px solid #EFEFF2', borderRadius: '8px', padding: '15px 17px' }}>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#000', marginBottom: '3px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#75757F', lineHeight: 1.45 }}>{body}</div>
    </div>
  );
}

function Rubric({ label, ok, note }: { label: string; ok: boolean; note?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 15px',
        border: `1px solid ${ok ? '#EFEFF2' : '#E6D9B0'}`,
        borderRadius: '8px',
        background: ok ? '#fff' : '#FFFBEE',
      }}
    >
      {ok ? (
        <Check size={18} color="#00A800" strokeWidth={2.4} style={{ flex: 'none' }} />
      ) : (
        <TriangleAlert size={18} color="#9A7B00" strokeWidth={2.2} style={{ flex: 'none' }} />
      )}
      <span style={{ fontSize: '14px', fontWeight: 700, width: '120px', flex: 'none' }}>{label}</span>
      {note && <span style={{ fontSize: '13.5px', color: ok ? '#75757F' : '#333544' }}>{note}</span>}
    </div>
  );
}
